import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatedNumber, ChartCard, Skeleton } from "@/components/reusable";
import { fetchHumanAttention } from "@/services/analyticsApi";
import type { HumanAttentionSegment } from "@/types/analytics";

const RING_COLOURS: Record<HumanAttentionSegment["level"], string> = {
  fully_automated: "var(--color-brand-primary)",
  light_touch: "#378ADD",
  heavy_touch: "#EAB308",
  blocked: "var(--color-error)",
};

interface ArcSpec {
  segment: HumanAttentionSegment;
  innerR: number;
  outerR: number;
  endAngle: number; // in radians, from -π/2 clockwise
}

/**
 * Concentric ring layout — each level gets its own ring, filled clockwise
 * by its share. Reads as "budget" better than a donut because the rings
 * visibly nest rather than compete for the same arc space.
 */
export function HumanAttentionRings() {
  const [segments, setSegments] = useState<HumanAttentionSegment[] | null>(null);

  useEffect(() => {
    let active = true;
    fetchHumanAttention().then((s) => {
      if (active) setSegments(s);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <ChartCard
      title="Human attention budget"
      subtitle="Where reviewer effort actually goes across recent files"
      height={500}
    >
      {!segments ? (
        <Skeleton height="h-full" className="min-h-[220px]" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5 items-center h-full">
          <RingDiagram segments={segments} />
          <ul className="flex flex-col gap-3">
            {segments.map((s) => (
              <li key={s.level} className="flex items-start gap-3">
                <span
                  className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{ background: RING_COLOURS[s.level] }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[12px] font-medium text-[var(--color-text-primary)]">
                      {s.label}
                    </span>
                    <AnimatedNumber
                      value={s.percent * 100}
                      format={(v) => `${v.toFixed(0)}%`}
                      className="text-[11px] text-[var(--color-text-tertiary)]"
                      style={{ fontFamily: "var(--font-mono)" }}
                    />
                  </div>
                  <div className="text-[11px] text-[var(--color-text-tertiary)]">
                    <AnimatedNumber
                      value={s.fileCount}
                      format={(v) => Math.round(v).toLocaleString()}
                    />{" "}
                    file{s.fileCount === 1 ? "" : "s"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </ChartCard>
  );
}

function RingDiagram({ segments }: { segments: HumanAttentionSegment[] }) {
  const size = 240;
  const center = size / 2;
  const maxR = size / 2 - 8;
  const ringThickness = (maxR - 20) / segments.length;

  const arcs = useMemo<ArcSpec[]>(() => {
    return segments.map((seg, i) => {
      const outerR = maxR - i * ringThickness;
      const innerR = outerR - ringThickness * 0.72;
      const endAngle = -Math.PI / 2 + seg.percent * Math.PI * 2;
      return { segment: seg, innerR, outerR, endAngle };
    });
  }, [segments, maxR, ringThickness]);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      {arcs.map(({ segment, innerR, outerR, endAngle }, i) => (
        <g key={segment.level}>
          {/* Track */}
          <path
            d={arcPath(center, center, innerR, outerR, Math.PI * 1.5)}
            fill="var(--color-bg-tertiary)"
          />
          {/* Swept fill */}
          <SweepingRing
            cx={center}
            cy={center}
            innerR={innerR}
            outerR={outerR}
            endAngle={endAngle}
            colour={RING_COLOURS[segment.level]}
            delayMs={120 + i * 100}
          />
        </g>
      ))}
      {/* Centre label */}
      <text
        x={center}
        y={center - 4}
        textAnchor="middle"
        className="fill-[var(--color-text-tertiary)]"
        style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}
      >
        Fully automated
      </text>
      <text
        x={center}
        y={center + 14}
        textAnchor="middle"
        className="fill-[var(--color-text-primary)]"
        style={{ fontSize: 22, fontWeight: 600, fontFamily: "var(--font-mono)" }}
      >
        {((segments[0]?.percent ?? 0) * 100).toFixed(0)}%
      </text>
    </svg>
  );
}

function arcPath(cx: number, cy: number, rIn: number, rOut: number, endAngle: number): string {
  const start = -Math.PI / 2;
  const total = endAngle - start;
  if (total <= 0.0001) return "";
  const large = total > Math.PI ? 1 : 0;
  const x0 = cx + rOut * Math.cos(start);
  const y0 = cy + rOut * Math.sin(start);
  const x1 = cx + rOut * Math.cos(endAngle);
  const y1 = cy + rOut * Math.sin(endAngle);
  const x2 = cx + rIn * Math.cos(endAngle);
  const y2 = cy + rIn * Math.sin(endAngle);
  const x3 = cx + rIn * Math.cos(start);
  const y3 = cy + rIn * Math.sin(start);
  return [
    `M ${x0} ${y0}`,
    `A ${rOut} ${rOut} 0 ${large} 1 ${x1} ${y1}`,
    `L ${x2} ${y2}`,
    `A ${rIn} ${rIn} 0 ${large} 0 ${x3} ${y3}`,
    "Z",
  ].join(" ");
}

interface SweepingRingProps {
  cx: number;
  cy: number;
  innerR: number;
  outerR: number;
  endAngle: number;
  colour: string;
  delayMs: number;
  durationMs?: number;
}

/**
 * Arc that sweeps from the 12 o'clock position clockwise to its final
 * endAngle on mount. On subsequent updates, tweens from the currently
 * displayed angle to the new endAngle.
 *
 * Can't use framer's `d` string tween here — the zero-length arc path
 * generated by `arcPath` returns an empty string (degenerate topology),
 * so framer has nothing to interpolate from. Direct ref + RAF + per-frame
 * `d` regeneration is the reliable approach.
 */
function SweepingRing({
  cx,
  cy,
  innerR,
  outerR,
  endAngle,
  colour,
  delayMs,
  durationMs = 800,
}: SweepingRingProps) {
  const prevAngleRef = useRef<number>(-Math.PI / 2);
  const [d, setD] = useState<string>("");

  useEffect(() => {
    const fromAngle = prevAngleRef.current;
    const toAngle = endAngle;

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      prevAngleRef.current = toAngle;
      setD(arcPath(cx, cy, innerR, outerR, toAngle));
      return;
    }

    if (Math.abs(toAngle - fromAngle) < 0.0001) return;

    let startTime: number | null = null;
    let rafId: number;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime - delayMs;
      if (elapsed < 0) {
        rafId = requestAnimationFrame(step);
        return;
      }
      const t = Math.min(1, elapsed / durationMs);
      const eased = ease(t);
      const currentAngle = fromAngle + (toAngle - fromAngle) * eased;
      prevAngleRef.current = currentAngle;
      setD(arcPath(cx, cy, innerR, outerR, currentAngle));
      if (t < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [endAngle, cx, cy, innerR, outerR, delayMs, durationMs]);

  return <path d={d} fill={colour} opacity={0.92} />;
}
